/**
 * Componente: DoctorApprovalTable
 * Exibe uma tabela de médicos pendentes de aprovação
 */

import { Check, X, Eye, AlertCircle, FileCheck, FileText, Video } from 'lucide-react';
import { DoctorDTO } from '@/services';
import {
  buildDoctorReviewChecklist,
  formatDoctorCrmDisplay,
} from '@/lib/adminDoctorReview';

interface DoctorApprovalTableProps {
  doctors: DoctorDTO[];
  isLoading?: boolean;
  onApprove?: (doctorId: string) => void;
  onReject?: (doctorId: string) => void;
  onView?: (doctorId: string) => void;
}

export function DoctorApprovalTable({
  doctors,
  isLoading = false,
  onApprove,
  onReject,
  onView,
}: DoctorApprovalTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-3" />
        <p className="text-gray-600 dark:text-gray-400">Carregando médicos...</p>
      </div>
    );
  }

  if (!doctors || doctors.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-800 p-12 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          Nenhum médico pendente de aprovação
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-neutral-800/50 border-b border-gray-200 dark:border-neutral-800">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                Nome do Médico
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                Especialidade
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                Data de Candidatura
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                CRM
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                Documentos
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white">
                Status
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
            {doctors.map((doctor) => {
              const review = buildDoctorReviewChecklist(
                doctor,
                doctor.documents || [],
              );
              return (
              <tr
                key={doctor.id}
                className="hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {(doctor.user?.name?.charAt(0) || 'D').toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {doctor.user?.name || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {doctor.user?.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                  {doctor.specialty}
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                  {new Date(doctor.createdAt).toLocaleDateString('pt-BR')}
                </td>
                <td className="px-6 py-4">
                  <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                    {formatDoctorCrmDisplay(doctor.crm, doctor.crmState)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {doctor.documents && doctor.documents.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                        <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
                          {doctor.documents.length}{' '}
                          {doctor.documents.length === 1 ? 'Doc' : 'Docs'}
                        </span>
                      </div>
                      {doctor.documents.some(
                        (d) =>
                          d.documentType === 'CONFIRMATION_VIDEO' ||
                          d.type === 'CONFIRMATION_VIDEO',
                      ) ? (
                        <div className="flex items-center gap-1 text-xs text-teal-700 dark:text-teal-300">
                          <Video className="w-3.5 h-3.5" />
                          Vídeo
                        </div>
                      ) : (
                        <span className="text-[11px] text-amber-600 dark:text-amber-400">
                          Sem vídeo
                        </span>
                      )}
                    </div>
                  ) : doctor.professionalDocumentsUrl ? (
                    <div className="flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-xs font-medium text-green-700 dark:text-green-300">
                        Legado
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span className="text-xs font-medium text-red-700 dark:text-red-300">
                        Pendente
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                      doctor.isApproved || doctor.status === 'APPROVED'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : doctor.status === 'REJECTED'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                    }`}
                  >
                    {doctor.isApproved || doctor.status === 'APPROVED'
                      ? 'Aprovado'
                      : doctor.status === 'REJECTED'
                        ? 'Recusado'
                        : 'Pendente'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {!doctor.isApproved && (
                      <>
                        <button
                          onClick={() => onApprove?.(doctor.id)}
                          disabled={!review.canApprove}
                          title={
                            review.canApprove
                              ? 'Aprovar Médico'
                              : review.approveBlockReason ||
                                'Requisitos incompletos para aprovação'
                          }
                          className={`p-2 rounded transition-colors ${
                            review.canApprove
                              ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer'
                              : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                          }`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onReject?.(doctor.id)}
                          title="Rejeitar Médico"
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => onView?.(doctor.id)}
                      title="Ver Detalhes"
                      className="p-2 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
